import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ActiveSection from './leftpanel-active-section';

import CollectionIcon from 'calcite-ui-icons-react/CollectionIcon';
import CursorPlusIcon from 'calcite-ui-icons-react/CursorPlusIcon';
import CursorSelectionIcon from 'calcite-ui-icons-react/CursorSelectionIcon';
import ImageSegmentationIcon from 'calcite-ui-icons-react/ImageSegmentationIcon';
import LayerReferenceIcon from 'calcite-ui-icons-react/LayersReferenceIcon';
import SplitIcon from 'calcite-ui-icons-react/PreserveIcon';
import QuestionIcon from 'calcite-ui-icons-react/QuestionIcon';
import InformationIcon from 'calcite-ui-icons-react/InformationIcon';
import FSCLogo from '../../assets/fsc-logo.svg';

import MapController from '../../controllers/MapController';

import { setTimelineOpen } from '../../reducers/auditdata/actions';
import { RootState } from '../../reducers/app';

import './leftpanel.scss';
import { IPerformAuditCriteriaData } from '../../types/auditdata';
import ga, { GACategories, GAActions } from '../../utils/googleAnalytics.utils';
import AuthController from '../../controllers/AuthController';

/**
 * Component for displaying, and interacting with, the app's Audit Data Panel
 */
const LeftPanel = ({
  timelineHasData,
  setMapLayersToggled,
  mapLayersToggled,
  setInfoModalActive,
  setShowTour,
  setIsResourcesOpen,
  isResourcesOpen
}: {
  timelineHasData: boolean | undefined;
  setMapLayersToggled: Function;
  mapLayersToggled: boolean;
  setInfoModalActive: Function;
  setShowTour: Function;
  setIsResourcesOpen: Function;
  isResourcesOpen: Boolean;
}) => {
  // LOCAL STATE
  const [selectedPanel, setSelectedPanel] = useState('');

  // const [mapLayersToggled, setMapLayersToggled] = useState(false);

  // REDUX
  const dispatch = useDispatch();
  const drawComplete = useSelector((state: RootState) => state.mapview.drawComplete);
  const timelineOpen = useSelector((state: RootState) => state.auditdata.timelineOpen);
  const creatingRiverBoundaries = useSelector((state: RootState) => state.auditdata.creatingRiverBoundaries);
  const auditedCriteria = useSelector((state: RootState) => state.mapview.performAuditLayers);


  useEffect(() => {
    if (drawComplete && timelineHasData) {
      togglePanel('analyze-imagery');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawComplete, timelineHasData]);

  /**
   * function to set which section of the map layers component should be opened or closed
   * @param panelName -- value of panel clicked on in component
   */
  const togglePanel = (panelName: string) => {
    // if we click same section that is active, we should close it
    if (panelName === selectedPanel) {
      panelName = '';
    }
    if (panelName !== 'analyze-imagery' && timelineOpen) {
      dispatch(setTimelineOpen(false));
    }

    if (panelName !== 'contextual-data' && MapController._riverLayer?.visible) {
      MapController.updateLayerVisibility(MapController._riverLayer.id, false);
    }

    if (panelName === 'contextual-data' && creatingRiverBoundaries && !MapController._riverLayer?.visible) {
      MapController.updateLayerVisibility(MapController._riverLayer!.id, true);
    }

    if (panelName !== '' && panelName !== selectedPanel) {
      ga.event(GACategories.Panel, GAActions.Opened, panelName);
    }

    setSelectedPanel(panelName);
    MapController.updateBoundingBoxImageVisibility(panelName === 'analyze-imagery');
  };

  const toggleResources = (isOpen: boolean) => {
    setIsResourcesOpen(isOpen)
    if (isOpen) {
      ga.event(GACategories.Modal, GAActions.Opened, "Resources");
    }
  }

  const toggleAbout = () => {
    setShowTour(true);
    ga.event(GACategories.Modal, GAActions.Opened, "About");
  }

  const toggleInfo = () => {
    setInfoModalActive()
    ga.event(GACategories.Modal, GAActions.Opened, "Info");
  }

  return (
    <div className='left-panel'>
      <div className='top'>
        <div className='left-panel-logo'>
          <img src={FSCLogo} alt='FSC Logo' />
          <div>
            GIS
            <br />
            Portal
          </div>
        </div>
        <div className='left-panel-tools'>
          <div
            id='map-layers'
            className={`left-panel-tool ${mapLayersToggled ? 'selected' : ''}`}
            onClick={() => setMapLayersToggled()}
            data-tut='step-one'
          >
            <CollectionIcon size={32} />
            <div className='tool-name'>
              Map
              <br />
              Layers
            </div>
          </div>
          <div
            id='select-area'
            className={`left-panel-tool ${selectedPanel === 'select-area' ? 'selected' : ''}`}
            onClick={() => togglePanel('select-area')}
            data-tut='step-two'
          >
            <CursorSelectionIcon size={32} />
            <div className='tool-name'>
              Select
              <br />
              Audit Area
            </div>
          </div>
          <div
            id='analyze-imagery'
            className={`left-panel-tool ${selectedPanel === 'analyze-imagery' ? 'selected' : ''}`}
            onClick={() => togglePanel('analyze-imagery')}
            data-tut='step-three'
          >
            <ImageSegmentationIcon size={32} />
            <div className='tool-name'>
              Analyze
              <br />
              Imagery
            </div>
          </div>
          <div
            id='contextual-data'
            className={`left-panel-tool ${selectedPanel === 'contextual-data' ? 'selected' : ''}`}
            onClick={() => togglePanel('contextual-data')}
            data-tut='step-four'
          >
            <CursorPlusIcon size={32} />
            <div className='tool-name'>
              Add Contextual
              <br />
              Data
            </div>
          </div>
          <div
            id='merge-intersect'
            className={`left-panel-tool ${selectedPanel === 'merge-intersect' ? 'selected' : ''}`}
            onClick={() => togglePanel('merge-intersect')}
            data-tut='step-five'
          >
            <SplitIcon size={32} />
            <div className='tool-name'>
              Merge and
              <br />
              Intersect Layers
            </div>
          </div>
          <div
            id='perform-audit'
            className={`left-panel-tool ${selectedPanel === 'perform-audit' ? 'selected' : ''}`}
            onClick={() => togglePanel('perform-audit')}
            data-tut='step-six'
          >
            <LayerReferenceIcon size={32} />
            <div className='tool-name'>Analyze Principles and Criteria</div>
          </div>
        </div>
      </div>

      <div className="bottom">
        <button className='green-button resources' onClick={() => toggleResources(!isResourcesOpen)}>
          <p>Resources</p>
        </button>
        <div className='left-panel-resources'>
          <div className='icon-container'>
            <div className='resource' id='question' onClick={() => toggleAbout()}>
              <QuestionIcon size={24} />
            </div>
            <div className='resource' id='info' onClick={() => toggleInfo()}>
              <InformationIcon size={24} />
            </div>
          </div>
        </div>
        <div className='account-wrapper'>
          <button onClick={() => AuthController.logout()}>Log Out</button>
        </div>
      </div>
      {selectedPanel && <ActiveSection selectedPanel={selectedPanel} mapLayersToggled={mapLayersToggled} />}
      {
        // We render these charts here because we need to ensure they are never unmounted (which happens if kept inside one of the left panel components). This ensures
        // that a user will never unintentionally crash the app when changing left panel components mid-audit
        auditedCriteria.length > 0 && auditedCriteria.map((pal: IPerformAuditCriteriaData) =>
          <div
            id={`criterion-${pal.criteriaNumber}-chart-hidden`}
            className='hidden-chart-to-print'
            key={`criterion-${pal.criteriaNumber}-chart-hidden`}
          />
        )
      }
    </div>
  );
};

export default LeftPanel;
